import { useCallback, useEffect, useRef, useState } from "react";
import { useAppFeedback } from "../../useAppFeedback";
import { AuthUser } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { EventSummary } from "../../../services/drinkService";
import { fetchDrinkActivities } from "../../../services/drinks/DrinkService";
import { fetchActiveEvent, fetchOpenEvents } from "../../../services/drinks/EventService";

export const useProfileData = (
    user: AuthUser | null,
    isAuthenticated: boolean,
    t: ReturnType<typeof useTranslation>["t"]
) => {
    const { message } = useAppFeedback();
    const tRef = useRef(t);
    tRef.current = t;
    const profileRequest = useRef(0);
    const [totalDrinks, setTotalDrinks] = useState<number>(0);
    const [lastDrinkAt, setLastDrinkAt] = useState<Date | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [eventsLoading, setEventsLoading] = useState<boolean>(false);
    const [activeEvent, setActiveEvent] = useState<EventSummary | null>(null);
    const [openEvents, setOpenEvents] = useState<EventSummary[]>([]);
    const loadProfile = useCallback(async () => {
        if (!user) return;
        const request = ++profileRequest.current;
        setLoading(true);
        setProfileError(null);
        try {
            const activities = await fetchDrinkActivities(user);
            const total = activities.reduce((sum, activity) => sum + activity.amountMl, 0);
            const latest = activities.reduce<Date | null>((current, activity) => {
                if (!activity.timestamp) return current;
                if (!current || activity.timestamp > current) return activity.timestamp;
                return current;
            }, null);
            if (request === profileRequest.current) {
                setTotalDrinks(total);
                setLastDrinkAt(latest);
            }
        } catch {
            if (request !== profileRequest.current) return;
            const details = tRef.current("profile.messages.loadProfileError");
            setProfileError(details);
            message.error(details);
        } finally {
            if (request === profileRequest.current) setLoading(false);
        }
    }, [message, user]);

    useEffect(() => {
        if (isAuthenticated && user) void loadProfile();
        else {
            profileRequest.current++;
            setTotalDrinks(0);
            setLastDrinkAt(null);
            setProfileError(null);
            setLoading(false);
        }
    }, [isAuthenticated, user, loadProfile]);

    const loadEvents = useCallback(async () => {
        if (!user) {
            setActiveEvent(null);
            setOpenEvents([]);
            return;
        }

        setEventsLoading(true);
        try {
            const [current, open] = await Promise.all([
                fetchActiveEvent(user),
                fetchOpenEvents(user),
            ]);

            setActiveEvent(current);
            if (current) {
                setOpenEvents(open.filter((evt) => evt.eventId !== current.eventId));
            } else {
                setOpenEvents(open);
            }
        } catch {
            const details = tRef.current("profile.messages.loadEventsError");
            message.error(details);
        } finally {
            setEventsLoading(false);
        }
    }, [user, message]);

    useEffect(() => {
        if (isAuthenticated && user) {
            loadEvents();
        }
    }, [isAuthenticated, user, loadEvents]);

    return { totalDrinks, lastDrinkAt, loading, profileError, loadProfile, eventsLoading, setEventsLoading, activeEvent, openEvents, setActiveEvent, setOpenEvents, loadEvents };
};
